import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

import RichTextArea from "components/common/RichTextArea";
import { htmlToLines, htmlToText, sanitizeRichText, textToHtml } from "components/common/richText";
import { Close } from "./RxIcons";

/**
 * Card · Custom instruction (Figma 3031:9756). The staffer writes free pointers
 * that land under "Other Instructions" in the document.
 *
 * ONE BOX = ONE INSTRUCTION (product, 2026-09-16: "there can be multiple added
 * and removed also — previously the removal was not possible"):
 *   · "Add new" appends an empty box and puts the caret in it;
 *   · "Add to prescription" puts every NEW box that has text on the
 *     prescription;
 *   · a box already ON the prescription is live: what it shows is what prints
 *     and what publishes — an edit applies as you type (after a short pause, and
 *     at once when the box loses focus). An emptied box prints nothing until it
 *     is removed;
 *   · × removes a box, taking it off the prescription at once (there is no
 *     confirm frame on the board, and × everywhere else in the builder is
 *     immediate).
 * A new box left unadded, or any box over the limit, is reported through
 * `onPendingChange`, so the screen can refuse to publish rather than silently
 * leave it out. The board's shared toolbar row is NOT drawn (the user's call):
 * each box keeps the RichTextArea's own toolbar, which is how bold/italic work.
 *
 * FORMATTING IS AUTHORING-ONLY, and that is a backend constraint, not a
 * shortcut: api-prescription-service renders the PDF through Go's
 * `html/template` with `Instruction` typed as a plain *string, so markup is
 * auto-escaped and a bolded phrase would reach the patient as the literal
 * characters "<B>INSULIN DOSE</B>". So every box is FLATTENED to its lines
 * (each line or list item its own numbered pointer, which is what "Other
 * Instructions" already is), and nothing downstream ever sees HTML: not the
 * entries, not the document preview, not the wire. A box already on the
 * prescription re-seeds from those lines, so after a detour its bold is gone
 * but its text and line structure are not.
 *
 * The entries — `{ id, lines }`, in prescription order — are owned by the
 * screen (parked in the scratch across "+ Add …" detours, flattened for the
 * document with `instructionLines`); the boxes follow that order. Boxes not yet
 * added are local to this card, as the single field's draft always was.
 */

/** Nothing bounds a pointer server-side, and the PDF cell doesn't wrap well. */
export const MAX_INSTRUCTION_CHARS = 500;
/** How long a pause in typing applies an edit to an instruction already added. */
const APPLY_AFTER_MS = 250;
/**
 * How much of a box's markup is worth parking across a detour. Past it the box
 * is parked as its TEXT instead: the scratch shares one sessionStorage key with
 * the rest of the builder, and a write that throws on quota leaves NOTHING
 * parked (rxScratch clears rather than replay a stale copy), so one runaway box
 * must not cost the doctor the whole builder. Formatting is the only casualty,
 * and it dissolves on add anyway.
 */
const MAX_DRAFT_HTML = 20000;

let seq = 0;
/** A stable id for an instruction — the box's React key AND the entry's id, so
    adding a box never remounts its (uncontrolled) editor. */
export const newInstructionId = () => `ci-${Date.now().toString(36)}-${(seq += 1)}`;

const cleanLines = (lines) =>
  lines
    .map((l) => (typeof l === "string" ? l.replace(/\s+/g, " ").trim() : ""))
    .filter(Boolean);

/**
 * Parked or seeded instructions -> entries. The scratch used to hold plain
 * strings, one per pointer; each becomes a one-line entry, so a prescription
 * parked before this change restores intact. Lines are normalised the way the
 * editor reads them back, so a restored box never looks edited.
 */
export const toInstructionEntries = (list) =>
  (Array.isArray(list) ? list : [])
    .map((e) => {
      if (typeof e === "string") {
        const lines = cleanLines([e]);
        return lines.length ? { id: newInstructionId(), lines } : null;
      }
      if (e && Array.isArray(e.lines)) {
        /* An entry the doctor emptied but did not remove keeps its box — its
           id is what says the box existed; only a blank parked STRING is
           dropped. It prints nothing either way. */
        return { id: e.id || newInstructionId(), lines: cleanLines(e.lines) };
      }
      return null;
    })
    .filter(Boolean);

/** Entries -> the flat pointer list the document and the wire use. */
export const instructionLines = (entries) =>
  (entries || []).reduce((acc, e) => acc.concat((e && e.lines) || []), []);

const sameLines = (a, b) => JSON.stringify(a || []) === JSON.stringify(b || []);

const committedBox = (entry) => ({
  id: entry.id,
  committed: true,
  saved: entry.lines,
  seed: textToHtml(entry.lines.join("\n")),
  html: textToHtml(entry.lines.join("\n")),
});
const draftBox = () => ({ id: newInstructionId(), committed: false, saved: [], seed: "", html: "" });

const CustomInstructionCard = ({
  instructions,
  drafts,
  onAdd,
  onUpdate,
  onRemove,
  onPendingChange,
  onDraftsChange,
}) => {
  /* The empty box the card puts up by itself (nothing on the prescription yet,
     or the last box removed). Only THAT box is swept when a restore lands —
     never one the doctor asked for with "Add new". */
  const placeholderId = useRef(null);
  const placeholder = () => {
    const b = draftBox();
    placeholderId.current = b.id;
    return b;
  };
  const [boxes, setBoxes] = useState(() =>
    instructions && instructions.length ? instructions.map(committedBox) : [placeholder()]
  );
  const boxesRef = useRef(boxes);
  boxesRef.current = boxes;
  /** A box id to put the caret in, or "add-new" for the Add new button. */
  const [focusTarget, setFocusTarget] = useState(null);
  const cardRef = useRef(null);
  const addNewRef = useRef(null);
  const timers = useRef({});
  const draftsApplied = useRef(false);
  useEffect(() => {
    const pending = timers.current;
    return () => Object.keys(pending).forEach((id) => clearTimeout(pending[id]));
  }, []);

  /* Follow the screen's entries — the ONE order. A scratch restore lands AFTER
     this card is on screen (the builder's fetches settle first), and a removal
     elsewhere must not leave a box behind. Committed boxes are laid out in
     `instructions` order (so the card, the document and the wire agree), boxes
     not yet added follow in their own order; an entry whose lines changed
     updates what its box is compared against, without re-seeding its editor. */
  useEffect(() => {
    setBoxes((prev) => {
      const byId = {};
      (instructions || []).forEach((e) => {
        byId[e.id] = e;
      });
      const prevById = {};
      prev.forEach((b) => {
        prevById[b.id] = b;
      });
      const committed = (instructions || []).map((e) => {
        const b = prevById[e.id];
        if (!b) return committedBox(e);
        return b.committed && sameLines(b.saved, e.lines) ? b : { ...b, committed: true, saved: e.lines };
      });
      const arrived = (instructions || []).some((e) => !prevById[e.id]);
      let unaddedBoxes = prev.filter((b) => !byId[b.id] && !b.committed);
      if (arrived) {
        // A restore replacing the card's own empty box leaves no stray blank —
        // unless the doctor is typing in it.
        unaddedBoxes = unaddedBoxes.filter((b) => {
          if (b.id !== placeholderId.current || htmlToLines(b.html).length) return true;
          const el = cardRef.current && cardRef.current.querySelector(`[data-instr-id="${b.id}"]`);
          return !!(el && el.contains(document.activeElement));
        });
      }
      const next = committed.concat(unaddedBoxes);
      if (!next.length) return [placeholder()];
      const same = next.length === prev.length && next.every((b, i) => b === prev[i]);
      return same ? prev : next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instructions]);

  /* Boxes typed but NOT added are parked too (the screen hands them back as
     `drafts`), so a "+ Add …" detour cannot silently destroy text the doctor
     has written — the very detour this card sits beside. Applied once: they are
     the doctor's own unfinished boxes, not a live feed. */
  useEffect(() => {
    if (draftsApplied.current || !drafts || !drafts.length) return;
    draftsApplied.current = true;
    setBoxes((prev) => {
      const have = {};
      prev.forEach((b) => {
        have[b.id] = true;
      });
      const arrivals = drafts
        .filter((d) => d && d.id && !have[d.id] && htmlToLines(d.html).length)
        .map((d) => {
          /* SANITISED here, not trusted: this is the only html the card seeds
             that it did not derive itself (the committed boxes go through
             textToHtml), and RichTextArea is deliberately not the safety
             boundary — its seed is written straight into a live DOM, where an
             onerror in a parked box would run. */
          const html = sanitizeRichText(d.html);
          return { id: d.id, committed: false, saved: [], seed: html, html };
        });
      if (!arrivals.length) return prev;
      const kept = prev.filter((b) => {
        if (b.committed || b.id !== placeholderId.current || htmlToLines(b.html).length) return true;
        // Same rule as the entries effect: never sweep the box being typed in.
        const el = cardRef.current && cardRef.current.querySelector(`[data-instr-id="${b.id}"]`);
        return !!(el && el.contains(document.activeElement));
      });
      return kept.concat(arrivals);
    });
  }, [drafts]);

  useEffect(() => {
    if (!focusTarget) return;
    if (focusTarget === "add-new") {
      if (addNewRef.current) addNewRef.current.focus();
    } else if (cardRef.current) {
      const el = cardRef.current.querySelector(`[data-instr-id="${focusTarget}"] .rt-editor`);
      if (el) el.focus();
    }
    setFocusTarget(null);
  }, [focusTarget, boxes]);

  const rows = boxes.map((b) => {
    const lines = htmlToLines(b.html);
    const chars = htmlToText(b.html).length;
    return {
      ...b,
      lines,
      chars,
      tooLong: chars > MAX_INSTRUCTION_CHARS,
      unadded: !b.committed && lines.length > 0,
    };
  });
  const unadded = rows.filter((r) => r.unadded);
  const tooLongCount = rows.filter((r) => r.tooLong).length;
  const canAdd = unadded.length > 0 && !unadded.some((r) => r.tooLong);

  useEffect(() => {
    if (onPendingChange) onPendingChange({ unadded: unadded.length, tooLong: tooLongCount });
  }, [unadded.length, tooLongCount, onPendingChange]);

  /* Reported as {id, html} so a restore can put the box back exactly as typed,
     bold and all — this is the card's own markup, never the wire's. */
  const draftKey = unadded.map((r) => `${r.id}:${r.html}`).join("|");
  useEffect(() => {
    if (onDraftsChange) {
      onDraftsChange(
        unadded.map((r) => ({
          id: r.id,
          // Over the cap the TEXT is parked, so the words survive even when the
          // markup would not fit (see MAX_DRAFT_HTML).
          html: r.html.length > MAX_DRAFT_HTML ? textToHtml(r.lines.join("\n")) : r.html,
        }))
      );
    }
    // draftKey is the content of `unadded`; comparing the array would re-fire
    // on every keystroke's new identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftKey, onDraftsChange]);

  /* An instruction already on the prescription: apply its current text. Not
     while it is over the limit — the screen refuses to publish instead. */
  const applyEdit = (id) => {
    clearTimeout(timers.current[id]);
    delete timers.current[id];
    const b = boxesRef.current.find((x) => x.id === id);
    if (!b || !b.committed) return;
    if (htmlToText(b.html).length > MAX_INSTRUCTION_CHARS) return;
    const lines = htmlToLines(b.html);
    if (sameLines(lines, b.saved)) return;
    setBoxes((prev) => prev.map((x) => (x.id === id ? { ...x, saved: lines } : x)));
    onUpdate(id, lines);
  };

  const setHtml = (id, html) => {
    setBoxes((prev) => prev.map((b) => (b.id === id ? { ...b, html } : b)));
    const b = boxesRef.current.find((x) => x.id === id);
    if (b && b.committed) {
      clearTimeout(timers.current[id]);
      timers.current[id] = setTimeout(() => applyEdit(id), APPLY_AFTER_MS);
    }
  };

  const addNew = () => {
    const box = draftBox();
    setBoxes((prev) => prev.concat(box));
    setFocusTarget(box.id);
  };

  const remove = (row) => {
    clearTimeout(timers.current[row.id]);
    delete timers.current[row.id];
    const at = boxes.findIndex((b) => b.id === row.id);
    const rest = boxes.filter((b) => b.id !== row.id);
    // Keep the keyboard user in the card: the box that took its place, the one
    // before it, or "Add new" when the card is back to its empty box.
    const neighbour = rest[at] || rest[at - 1];
    if (row.committed) onRemove(row.id);
    if (rest.length) {
      setBoxes(rest);
      setFocusTarget(neighbour ? neighbour.id : "add-new");
    } else {
      setBoxes([placeholder()]);
      setFocusTarget("add-new");
    }
  };

  const addToPrescription = () => {
    if (!canAdd) return;
    const added = unadded.map((r) => ({ id: r.id, lines: r.lines }));
    const addedIds = {};
    added.forEach((e) => {
      addedIds[e.id] = true;
    });
    setBoxes((prev) => {
      const next = prev
        // Unadded boxes left empty go with the add.
        .filter((b) => b.committed || addedIds[b.id])
        .map((b) => {
          if (!addedIds[b.id]) return b;
          const e = added.find((x) => x.id === b.id);
          return { ...b, committed: true, saved: e.lines };
        });
      return next.length ? next : [placeholder()];
    });
    onAdd(added);
    // The button disables itself on this commit, so focus would fall to <body>.
    setFocusTarget("add-new");
  };

  return (
    <section className="rx-card" ref={cardRef}>
      <header className="rx-card-head">
        <h3 className="rx-card-title">Custom instruction</h3>
        <span className="rx-card-meta">appears under “Other Instructions” in prescription</span>
        <div className="rx-card-head-end">
          <button type="button" className="rx-linkbtn" ref={addNewRef} onClick={addNew}>
            Add new
          </button>
        </div>
      </header>

      <div className="rx-instr-list">
        {rows.map((r, i) => (
          <div className="rx-instr-item" key={r.id} data-instr-id={r.id}>
            <div
              className="rx-instr-row"
              // Leaving an instruction already on the prescription applies its
              // edit at once, rather than after the typing pause.
              onBlur={r.committed ? () => applyEdit(r.id) : undefined}
            >
              <RichTextArea
                // Keyed on the box id, never the index: the editor is
                // uncontrolled, so an index key would show box 2 the text of
                // the box removed above it.
                className="rx-rich rx-instr-box"
                defaultHtml={r.seed}
                onChange={(html) => setHtml(r.id, html)}
                placeholder="Type an instruction — each line becomes its own pointer"
                ariaLabel={`Custom instruction ${i + 1}`}
              />
              <button
                type="button"
                className="rx-instr-x"
                aria-label={
                  r.committed
                    ? `Remove instruction ${i + 1} from the prescription`
                    : `Discard instruction ${i + 1}`
                }
                title={r.committed ? "Remove from the prescription" : "Discard"}
                onClick={() => remove(r)}
              >
                <Close />
              </button>
            </div>
            {r.tooLong ? (
              <p className="rx-foot-note is-error rx-instr-note">
                Too long — {r.chars} of {MAX_INSTRUCTION_CHARS} characters.
              </p>
            ) : null}
          </div>
        ))}
      </div>

      <div className="rx-card-foot is-end">
        <button type="button" className="rx-btn is-primary" disabled={!canAdd} onClick={addToPrescription}>
          Add to prescription
        </button>
      </div>
    </section>
  );
};
CustomInstructionCard.propTypes = {
  /** The prescription's custom instructions, in order: [{ id, lines: [string] }]. */
  instructions: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.string.isRequired, lines: PropTypes.arrayOf(PropTypes.string).isRequired })
  ).isRequired,
  /** New entries to append, in box order. */
  onAdd: PropTypes.func.isRequired,
  /** (id, lines) — an entry already on the prescription, edited. */
  onUpdate: PropTypes.func.isRequired,
  /** (id) — take an entry off the prescription. */
  onRemove: PropTypes.func.isRequired,
  /** Boxes typed but not added, from the parked scratch: [{ id, html }]. */
  drafts: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string, html: PropTypes.string })),
  /**
   * ({ unadded, tooLong }) — boxes that would NOT publish as shown. MUST be a
   * stable callback (a ref writer): it is an effect dependency.
   */
  onPendingChange: PropTypes.func,
  /** [{ id, html }] for the boxes typed but not added — parked by the screen. */
  onDraftsChange: PropTypes.func,
};

export default CustomInstructionCard;
